import { Link } from "react-router";
import { usePolling } from "../lib/use-polling";
import { api } from "../lib/api";
import { Table, type Column } from "../components/primitives/Table";
import { Badge } from "../components/primitives/Badge";
import { Pulse } from "../components/primitives/Pulse";
import { PageHelp } from "../components/PageHelp";

interface ConnectorRow {
  connectorType: string;
  label: string;
  capabilities: string[];
  ingest?: {
    webhook?: { signatureScheme: string };
    poll?: object;
    stream?: object;
    sqs?: object;
  };
  deliver?: unknown;
  lookup?: unknown;
}

interface TransformRow {
  id: string;
  sourceConnectorType: string;
  destConnectorType: string;
  sourceObjectType?: string;
  destObjectType?: string;
}

export function Catalog() {
  const connectors = usePolling(() => api.listConnectors(), null);
  const transforms = usePolling(() => api.listTransforms(), null);

  if (connectors.status === "loading" || transforms.status === "loading") return <Pulse />;

  const connCols: Column<ConnectorRow>[] = [
    {
      key: "type",
      label: "TYPE",
      mono: true,
      render: (r) => <Link to={`/catalog/connectors/${r.connectorType}`}>{r.connectorType}</Link>,
    },
    { key: "label", label: "LABEL", render: (r) => r.label },
    {
      key: "caps",
      label: "CAPABILITIES",
      render: (r) => (
        <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {r.capabilities.map((cap) => <Badge key={cap} tone="ok">{cap.toUpperCase()}</Badge>)}
        </span>
      ),
    },
  ];

  const xfCols: Column<TransformRow>[] = [
    {
      key: "id",
      label: "ID",
      mono: true,
      render: (r) => <Link to={`/catalog/transforms/${encodeURIComponent(r.id)}`}>{r.id}</Link>,
    },
    {
      key: "source",
      label: "SOURCE",
      mono: true,
      render: (r) => `${r.sourceConnectorType}:${r.sourceObjectType ?? "—"}`,
    },
    {
      key: "dest",
      label: "DEST",
      mono: true,
      render: (r) => `${r.destConnectorType}:${r.destObjectType ?? "—"}`,
    },
  ];

  return (
    <div>
      <div className="uppercase-label" style={{ marginBottom: 16 }}>CATALOG</div>
      <PageHelp />
      <div className="uppercase-label" style={{ marginBottom: 8 }}>CONNECTORS</div>
      <Table
        columns={connCols}
        rows={(connectors.data?.connectors ?? []) as ConnectorRow[]}
        rowKey={(r) => r.connectorType}
      />
      <div className="uppercase-label" style={{ marginTop: 32, marginBottom: 8 }}>TRANSFORMS</div>
      <Table
        columns={xfCols}
        rows={(transforms.data?.transforms ?? []) as TransformRow[]}
        rowKey={(r) => r.id}
      />
    </div>
  );
}
